import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'todoapp.theme';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): Theme | null {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === 'light' || saved === 'dark' ? saved : null;
  } catch {
    // localStorage bị chặn (chế độ riêng tư, chính sách trình duyệt) — vẫn chạy được, chỉ là
    // mỗi lần mở lại phải chọn chủ đề.
    return null;
  }
}

function systemTheme(): Theme {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Lần đầu vào thì theo cài đặt của hệ điều hành; sau khi người dùng tự chọn thì lấy lựa chọn đó.
  const [theme, setThemeState] = useState<Theme>(() => readStored() ?? systemTheme());

  useEffect(() => {
    // data-bs-theme là thứ Bootstrap 5.3 đọc để đổi màu form, nút, bảng. Toàn bộ biến màu riêng
    // của ứng dụng cũng khai báo theo thuộc tính này, nên chỉ cần một chỗ duy nhất để lật chủ đề.
    document.documentElement.setAttribute('data-bs-theme', theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);

    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* không lưu được thì lựa chọn chỉ sống trong phiên này */
    }
  }, []);

  const toggle = useCallback(() => setTheme(theme === 'dark' ? 'light' : 'dark'), [theme, setTheme]);

  const value = useMemo<ThemeContextValue>(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme phải được dùng bên trong ThemeProvider.');
  }

  return context;
}
